import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { MotionConfig, motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  MessageSquare, 
  BookOpen, 
  Database, 
  Activity, 
  FileText, 
  Settings,
  Sparkles,
  Zap,
  Cpu,
  ShieldCheck,
  Globe,
  Terminal,
  Layers
} from 'lucide-react';
import Dashboard from './pages/Dashboard';
import Messages from './pages/Messages';
import Facts from './pages/Facts';
import Analytics from './pages/Analytics';
import SettingsPage from './pages/Settings';
import Logs from './pages/Logs';
import Data from './pages/Data';
import Tools from './pages/Tools';
import { cn } from './lib/utils';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
  { to: '/messages', label: 'Chat & AI', icon: <MessageSquare size={18} /> },
  { to: '/tools', label: 'Tools & Health', icon: <Layers size={18} /> },
  { to: '/facts', label: 'Memory Facts', icon: <BookOpen size={18} /> },
  { to: '/data', label: 'Data Hub', icon: <Database size={18} /> },
  { to: '/analytics', label: 'Analytics', icon: <Activity size={18} /> },
  { to: '/logs', label: 'System Logs', icon: <FileText size={18} /> },
  { to: '/settings', label: 'Settings', icon: <Settings size={18} /> },
];

export default function App() {
  const [expanded, setExpanded] = useState(true);
  const [currentYear] = useState(() => new Date().getFullYear());

  return (
    <MotionConfig transition={{ type: "spring", damping: 25, stiffness: 200 }}>
      <BrowserRouter>
        <div className="flex min-h-screen bg-slate-950 text-slate-100 selection:bg-cyan-500/30 selection:text-cyan-200 relative overflow-hidden font-sans">
          
          <div className="fixed inset-0 pointer-events-none z-0">
            <div className="absolute -top-40 -left-40 w-96 h-96 bg-cyan-600/15 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/3 -right-40 w-96 h-96 bg-purple-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute -bottom-40 left-1/3 w-96 h-96 bg-emerald-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          </div>

          <motion.aside 
            initial={false}
            animate={{ width: expanded ? 250 : 72 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed left-0 top-0 h-full z-30 border-r border-white/10 bg-slate-900/70 backdrop-blur-2xl flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-3.5 px-5 py-6 border-b border-white/10">
                <motion.div 
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-tr from-cyan-500 via-blue-600 to-indigo-600 shadow-[0_0_20px_rgba(6,182,212,0.4)]"
                  whileHover={{ scale: 1.05, rotate: 5 }}
                >
                  <Sparkles size={22} className="text-white" />
                </motion.div>
                <div className={`overflow-hidden transition-all duration-300 ${expanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 hidden'}`}>
                  <div className="text-lg font-extrabold tracking-wider bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                    OLU
                  </div>
                  <div className="text-[10px] uppercase font-semibold text-cyan-400/80 tracking-widest">
                    Gabriel's AI Engine
                  </div>
                </div>
              </div>

              <nav className="flex flex-col gap-1.5 p-3 mt-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/dashboard'}
                    className={({ isActive }) => cn(
                      "flex items-center gap-3.5 px-3.5 py-3 rounded-xl text-sm font-medium transition-all duration-200 group relative",
                      isActive
                        ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/10 text-cyan-300 border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.15)]"
                        : "text-slate-400 hover:text-slate-200 hover:bg-white/5"
                    )}
                  >
                    <div className="transition-transform group-hover:scale-110">
                      {item.icon}
                    </div>
                    <span className={cn("whitespace-nowrap transition-all duration-200", !expanded && "hidden")}>
                      {item.label}
                    </span>
                  </NavLink>
                ))}
              </nav>
            </div>

            <div className="p-4 border-t border-white/10 flex flex-col gap-3">
              <div className="flex items-center gap-3 px-2">
                <div className="relative flex items-center justify-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  <div className="absolute w-4 h-4 rounded-full bg-emerald-400/40 animate-ping" />
                </div>
                <span className={cn("text-xs font-semibold text-emerald-400 tracking-wider uppercase", !expanded && "hidden")}>
                  System Active
                </span>
              </div>
              <div className={cn("text-[11px] text-slate-500 font-mono px-2", !expanded && "hidden")}>
                Copyright {currentYear} Gabriel Ajiboye
              </div>
            </div>

            <button
              onClick={() => setExpanded(!expanded)}
              className="absolute -right-3.5 top-8 w-7 h-7 flex items-center justify-center rounded-full bg-slate-900 border border-cyan-500/30 text-cyan-400 shadow-lg hover:bg-slate-800 hover:scale-110 transition-all z-40"
            >
              <svg 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.5"
                className={`transition-transform duration-300 ${expanded ? 'rotate-180' : 'rotate-0'}`}
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </motion.aside>

          <motion.main 
            initial={false}
            animate={{ marginLeft: expanded ? 250 : 72 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="flex-1 min-h-screen z-10 p-6 md:p-10"
          >
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/facts" element={<Facts />} />
              <Route path="/data" element={<Data />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/logs" element={<Logs />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
          </motion.main>
        </div>
      </BrowserRouter>
    </MotionConfig>
  );
}
